import { Button, ToastAction, useToast } from '@folio/components';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import {
  type EmailThreadDto,
  emailControllerAttachPolicies,
  emailControllerDetachPolicy,
} from 'korint-sdk';
import { useTranslation } from 'react-i18next';
import { Group } from '@folio/components/ui/flex';
import { emailsToAttach, invalidateEmailQueries } from './policyEmailThreads';

function useAttachEmails(policyId: string) {
  const { t } = useTranslation();
  const { toast } = useToast();
  const queryClient = useQueryClient();
  const { mutateAsync, isPending } = useMutation({
    mutationFn: async ({ emailIds, attach }: { emailIds: string[]; attach: boolean }) => {
      const results = await Promise.allSettled(
        emailIds.map(emailId =>
          attach
            ? emailControllerAttachPolicies(emailId, { policyIds: [policyId] })
            : emailControllerDetachPolicy(emailId, policyId),
        ),
      );
      const doneIds = emailIds.filter((_, index) => results[index]?.status === 'fulfilled');
      const failures = results.filter(result => result.status === 'rejected');
      if (doneIds.length === 0 && failures[0]) throw failures[0].reason;
      return { doneIds, failedCount: failures.length };
    },
    onSettled: () => {
      invalidateEmailQueries(queryClient);
      // Partial key: every opened thread detail
      queryClient.invalidateQueries({ queryKey: [{ url: '/email-threads/:threadId' }] });
    },
  });

  const attachEmails = (emailIds: string[]) =>
    mutateAsync({ emailIds, attach: true }).then(
      ({ doneIds: attachedIds, failedCount }) =>
        toast({
          variant: failedCount > 0 ? 'warning' : 'default',
          description: [
            t('policyExchanges.attachedCount', { count: attachedIds.length }),
            failedCount > 0 && t('policyExchanges.attachFailedCount', { count: failedCount }),
          ]
            .filter(Boolean)
            .join(' '),
          action: (
            <ToastAction
              altText={t('generalInbox.archive.undo')}
              onClick={() =>
                mutateAsync({ emailIds: attachedIds, attach: false }).then(
                  ({ failedCount }) =>
                    failedCount > 0 &&
                    toast({
                      variant: 'warning',
                      description: t('policyExchanges.detachFailedCount', { count: failedCount }),
                    }),
                  () => {},
                )
              }
            >
              {t('generalInbox.archive.undo')}
            </ToastAction>
          ),
        }),
      () => {},
    );
  return { attachEmails, isPending };
}

export function ToAttachBanner({
  threads,
  policyId,
}: {
  threads: EmailThreadDto[];
  policyId: string;
}) {
  const { t } = useTranslation();
  const { attachEmails, isPending } = useAttachEmails(policyId);
  const perThread = threads.map(thread => emailsToAttach(thread, policyId));
  const emailIds = perThread.flatMap(emails => emails.map(({ id }) => id));
  const threadCount = perThread.filter(emails => emails.length > 0).length;
  if (emailIds.length === 0) return null;

  return (
    <Group
      justify="between"
      gap={3}
      role="status"
      className="rounded-lg border border-amber-200 bg-amber-50 px-4 py-2.5 text-sm text-amber-900"
      data-testid="policy-exchanges-to-attach-banner"
    >
      <span>
        {t('policyExchanges.toAttachBanner', {
          count: emailIds.length,
          threads: t('policyExchanges.threadCount', { count: threadCount }),
        })}
      </span>
      <Button
        variant="outline"
        size="sm"
        className="shrink-0 border-amber-300 bg-card"
        disabled={isPending}
        onClick={() => attachEmails(emailIds)}
        data-testid="policy-exchanges-attach-all"
      >
        {t('policyExchanges.attachAll')}
      </Button>
    </Group>
  );
}
